import _ from 'lodash';
import type { z } from 'zod';
import { type TransformerConfig } from '../common/types';
import {
  createDecodeFromDbFn,
  createEncodeToDbFn,
  createRenameTransformer,
  createTransformer,
} from '../common/utils';
import { buildRecordId, encodeRecordId } from '../recordIds';
import { DBNotesCategorySchema, NotesCategorySchema } from './schemas';

type DBNotesCategory = z.infer<typeof DBNotesCategorySchema>;
type NotesCategory = z.infer<typeof NotesCategorySchema>;

export const NotesCategoryCodecConfig: TransformerConfig<
  typeof NotesCategorySchema,
  typeof DBNotesCategorySchema,
  'id'
> = {
  name: 'NotesCategory',
  clientSchema: NotesCategorySchema,
  dbSchema: DBNotesCategorySchema,
  transformers: [
    createRenameTransformer<typeof NotesCategorySchema, typeof DBNotesCategorySchema>(
      'code',
      'category'
    ),

    // A note is stored with its noteId and its code as key, and without its id, subject or
    // category code, which its category holds. Uses dependencies for partial updates where only
    // notes is provided
    createTransformer<typeof NotesCategorySchema, typeof DBNotesCategorySchema>({
      encoder: {
        sourceFields: ['notes'] as const,
        dependencies: ['notes'] as const,
        encode: ({ notes }) => ({
          notes: (notes as NotesCategory['notes']).map(
            ({ id, subject, categoryCode, code, ...note }) => ({
              ...note,
              key: code,
              noteId: `${subject}#${categoryCode}#${code}`,
            })
          ),
        }),
      },
      decoder: {
        sourceFields: ['subject', 'category', 'notes'] as const,
        decode: ({ subject, category, notes }) => ({
          ...(subject !== undefined && { subject }),
          // Each note's id needs its category's subject and code, so a read without them
          // gives no notes
          ...(subject !== undefined &&
            category !== undefined &&
            notes !== undefined && {
              notes: (notes as (DBNotesCategory['notes'][number] | null)[]).map(note => ({
                ..._.omit(note, ['noteId', 'key']),
                // A stored entry that is not an object reads with its parts alone, for the
                // read's check to report, rather than failing the decoder
                code: note?.key as string,
                id: encodeRecordId('note', { subject, categoryCode: category, code: note?.key }),
                subject,
                categoryCode: category,
              })) as NotesCategory['notes'],
            }),
        }),
      },
    }),
  ],
  derived: { id: category => buildRecordId('notesCategory', category) },
};

export const encodeNotesCategoryToDb = createEncodeToDbFn(NotesCategoryCodecConfig);
export const decodeNotesCategoryFromDb = createDecodeFromDbFn(NotesCategoryCodecConfig);
